/**
 * Helpers for the warehouse call-off wizard stock grid (WarehouseDeliveryModal):
 * local (per-browser) column preferences + small cell/row formatters. Extracted
 * so the modal stays within the file-size limit and the column-prefs logic can
 * be unit-tested in isolation.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import type { StockPlanningItem } from '../../../lib/api';
import type { LocalColumn } from './DirectDeliveryColumns';

/** Red when any health value is negative, amber when low, otherwise no tint. */
export function getStockHealthColor(min: string, max: string): string {
  const minVal = parseFloat(min) || 0;
  const maxVal = parseFloat(max) || 0;
  if (minVal < 0 || maxVal < 0) return 'bg-red-50';
  if (minVal < 50 || maxVal < 50) return 'bg-yellow-50';
  return '';
}

/** Get a cell value from a stock item based on column id. */
export function getItemCellValue(item: StockPlanningItem, colId: string): string {
  const val = item[colId as keyof StockPlanningItem];
  return val !== undefined && val !== null ? String(val) : '-';
}

const COLUMN_PREFS_KEY = 'call-off:warehouse-wizard:columns';

/**
 * Merge persisted column prefs with the canonical defaults. New columns added
 * since the prefs were saved fall back to their default visibility and sort
 * order so we don't permanently hide them.
 */
function mergeWithDefaults(defaults: LocalColumn[], saved: Partial<LocalColumn>[] | null): LocalColumn[] {
  if (!saved || !Array.isArray(saved)) return defaults;
  const byId = new Map(saved.map(c => [c.id, c]));
  return defaults.map(d => {
    const s = byId.get(d.id);
    return s
      ? { ...d, visible: s.visible ?? d.visible, sortOrder: s.sortOrder ?? d.sortOrder }
      : d;
  });
}

function loadStoredColumns(defaults: LocalColumn[]): LocalColumn[] {
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(COLUMN_PREFS_KEY);
    return mergeWithDefaults(defaults, raw ? JSON.parse(raw) : null);
  } catch {
    return defaults;
  }
}

/**
 * Manage the wizard's column visibility + order, persisted per-browser in
 * localStorage. Returns the full column list, the visible-in-order subset, and
 * toggle / move-up mutators.
 */
export function useLocalColumns(defaults: LocalColumn[]) {
  const [columns, setColumns] = useState<LocalColumn[]>(() => loadStoredColumns(defaults));

  useEffect(() => {
    try {
      const slim = columns.map(c => ({ id: c.id, visible: c.visible, sortOrder: c.sortOrder }));
      window.localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(slim));
    } catch {
      // localStorage full or disabled — silently ignore.
    }
  }, [columns]);

  const toggle = useCallback((columnId: string) => {
    setColumns(prev => prev.map(c =>
      c.id === columnId ? { ...c, visible: !c.visible } : c
    ));
  }, []);

  const moveUp = useCallback((columnId: string) => {
    setColumns(prev => {
      const sorted = [...prev].sort((a, b) => a.sortOrder - b.sortOrder);
      const idx = sorted.findIndex(c => c.id === columnId);
      if (idx <= 0) return prev;
      return sorted.map((col, i) => {
        if (i === idx) return { ...col, sortOrder: i - 1 };
        if (i === idx - 1) return { ...col, sortOrder: i + 1 };
        return { ...col, sortOrder: i };
      });
    });
  }, []);

  const visible = useMemo(
    () => [...columns].sort((a, b) => a.sortOrder - b.sortOrder).filter(c => c.visible),
    [columns]
  );

  return { columns, visible, toggle, moveUp };
}
